<!--
  TagManageModal — G1.7 widget parity for admin's TagManageModalComponent.

  Cross-conversation catalog management: rename, recolor, delete.
  Per-row affordances mirror the admin version:
    - Inline rename: edit in place; Save/Cancel pair appears when dirty.
      Enter commits, Escape reverts.
    - Native color picker: fires on `change` (close-of-picker) so we
      don't PUT per drag step.
    - Two-click delete: trash → red "Confirm?" pill for 3s → second
      click fires DELETE. Auto-reverts on timeout or other interaction.

  Mounted from ConversationHeader after the picker emits `manage`.
  Closes on backdrop click, Escape, or the explicit X.
-->
<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { tagsList, type Tag } from '../lib/api/queries.js';
  import { updateTagMutation, deleteTagMutation } from '../lib/api/mutations.js';

  type Props = { onClose: () => void };
  let { onClose }: Props = $props();

  const queryClient = useQueryClient();
  const catalogQuery = $derived(createQuery(tagsList()));
  const update = createMutation(updateTagMutation(queryClient));
  const remove = createMutation(deleteTagMutation(queryClient));

  // Alphabetical for consistent scan order. Sort on the derived list, not
  // in-place, so the underlying cache stays untouched.
  const sortedCatalog = $derived(
    [...($catalogQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
  );

  // Per-row inline-rename buffer. null = mirrors persisted name.
  let draftNames = $state<Record<string, string>>({});
  // Per-row error messages (mostly 409 slug collisions).
  let rowErrors = $state<Record<string, string>>({});
  // Two-click delete confirm tracker; resets after 3s.
  let confirmDeleteId = $state<string | null>(null);
  let confirmTimer: ReturnType<typeof setTimeout> | null = null;
  // Tag id whose row is currently in-flight (disables row controls).
  let busyTagId = $state<string | null>(null);
  let errorMessage = $state<string | null>(null);

  function draftName(id: string): string | null {
    return draftNames[id] ?? null;
  }

  function onNameInput(id: string, event: Event) {
    const value = (event.target as HTMLInputElement).value;
    draftNames = { ...draftNames, [id]: value };
  }

  function cancelRename(id: string) {
    const next = { ...draftNames };
    delete next[id];
    draftNames = next;
    clearRowError(id);
  }

  async function commitRename(tag: Tag) {
    const draft = draftName(tag.id);
    if (draft === null) return;
    const next = draft.trim();
    if (next.length === 0) {
      setRowError(tag.id, 'Name must not be empty.');
      return;
    }
    if (next === tag.name) {
      cancelRename(tag.id);
      return;
    }
    busyTagId = tag.id;
    clearRowError(tag.id);
    try {
      await $update.mutateAsync({ id: tag.id, name: next });
      cancelRename(tag.id);
    } catch (err) {
      setRowError(tag.id, err instanceof Error ? err.message : 'Unknown error.');
    } finally {
      busyTagId = null;
    }
  }

  async function onColorChange(tag: Tag, event: Event) {
    const nextColor = (event.target as HTMLInputElement).value;
    if (nextColor === tag.color) return;
    busyTagId = tag.id;
    clearRowError(tag.id);
    try {
      await $update.mutateAsync({ id: tag.id, color: nextColor });
    } catch (err) {
      setRowError(tag.id, err instanceof Error ? err.message : 'Unknown error.');
      // Revert input visually so swatch matches server state.
      (event.target as HTMLInputElement).value = tag.color;
    } finally {
      busyTagId = null;
    }
  }

  function startDeleteConfirm(id: string) {
    confirmDeleteId = id;
    if (confirmTimer !== null) clearTimeout(confirmTimer);
    confirmTimer = setTimeout(() => {
      confirmDeleteId = null;
      confirmTimer = null;
    }, 3000);
  }

  async function commitDelete(tag: Tag) {
    if (confirmTimer !== null) {
      clearTimeout(confirmTimer);
      confirmTimer = null;
    }
    confirmDeleteId = null;
    busyTagId = tag.id;
    clearRowError(tag.id);
    try {
      await $remove.mutateAsync({ id: tag.id });
    } catch (err) {
      setRowError(tag.id, err instanceof Error ? err.message : 'Unknown error.');
    } finally {
      busyTagId = null;
    }
  }

  function onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) onClose();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') onClose();
  }
  onMount(() => document.addEventListener('keydown', handleKeydown));
  onDestroy(() => document.removeEventListener('keydown', handleKeydown));

  function setRowError(id: string, msg: string) {
    rowErrors = { ...rowErrors, [id]: msg };
  }
  function clearRowError(id: string) {
    if (!(id in rowErrors)) return;
    const next = { ...rowErrors };
    delete next[id];
    rowErrors = next;
  }
</script>

<div
  class="backdrop"
  role="dialog"
  aria-modal="true"
  aria-labelledby="tag-manage-title"
  tabindex="-1"
  onclick={onBackdropClick}
  onkeydown={(e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  }}
>
  <div class="modal">
    <header class="modal-header">
      <strong id="tag-manage-title">Manage tags</strong>
      <button type="button" class="close-btn" aria-label="Close tag management" onclick={onClose}>×</button>
    </header>

    {#if $catalogQuery.isPending}
      <div class="empty" role="status">Loading...</div>
    {:else if $catalogQuery.error}
      <div class="empty error-text">Could not load tags.</div>
    {:else if sortedCatalog.length === 0}
      <div class="empty">
        <strong>No tags yet</strong>
        <p>Create tags from the picker on any conversation.</p>
      </div>
    {:else}
      <ul class="catalog" role="list">
        {#each sortedCatalog as t (t.id)}
          <li class="row">
            <input
              type="color"
              class="color-input"
              value={t.color}
              disabled={busyTagId === t.id}
              aria-label={`Color for ${t.name}`}
              onchange={(e) => onColorChange(t, e)}
            />
            <input
              type="text"
              class="name-input"
              value={draftName(t.id) ?? t.name}
              disabled={busyTagId === t.id}
              maxlength="64"
              aria-label={`Rename ${t.name}`}
              oninput={(e) => onNameInput(t.id, e)}
              onkeydown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void commitRename(t);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  cancelRename(t.id);
                }
              }}
            />
            {#if draftName(t.id) !== null && draftName(t.id) !== t.name}
              <button
                type="button"
                class="save-btn"
                onclick={() => void commitRename(t)}
                disabled={busyTagId === t.id}
              >Save</button>
              <button
                type="button"
                class="cancel-btn"
                onclick={() => cancelRename(t.id)}
              >Cancel</button>
            {:else if confirmDeleteId === t.id}
              <button
                type="button"
                class="confirm-btn"
                onclick={() => void commitDelete(t)}
                disabled={busyTagId === t.id}
              >Confirm?</button>
            {:else}
              <button
                type="button"
                class="trash-btn"
                aria-label={`Delete ${t.name}`}
                onclick={() => startDeleteConfirm(t.id)}
              >🗑</button>
            {/if}
            {#if rowErrors[t.id]}
              <span class="row-error" role="alert">{rowErrors[t.id]}</span>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}

    {#if errorMessage}
      <div class="footer-error" role="alert">{errorMessage}</div>
    {/if}

    <footer class="modal-footer">
      <p class="hint">
        Renaming or recoloring updates all conversations with this tag. Deleting
        removes the tag from every conversation.
      </p>
    </footer>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 50;
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 50%, transparent);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
  }
  .modal {
    width: 100%;
    max-width: 480px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 10px;
    box-shadow:
      0 4px 12px oklch(20% 0.01 250 / 0.12),
      0 24px 48px oklch(20% 0.01 250 / 0.2);
    font-family: var(--widget-font);
    font-size: 12.5px;
    color: var(--widget-fg, oklch(20% 0.01 250));
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    border-bottom: 1px solid var(--widget-divider, oklch(93% 0.006 250));
  }
  .modal-header strong {
    font-size: 13.5px;
    font-weight: 600;
  }
  .close-btn {
    background: none;
    border: none;
    font-size: 20px;
    line-height: 1;
    padding: 0 6px;
    cursor: pointer;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .close-btn:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .close-btn:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: 1px;
    border-radius: 4px;
  }

  .empty {
    padding: 32px 16px;
    text-align: center;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .empty.error-text {
    color: var(--widget-danger, oklch(55% 0.2 25));
  }
  .empty strong {
    display: block;
    font-size: 13.5px;
    font-weight: 600;
    color: var(--widget-fg, oklch(20% 0.01 250));
    margin-bottom: 4px;
  }
  .empty p {
    margin: 0;
    font-size: 12.5px;
  }

  .catalog {
    list-style: none;
    margin: 0;
    padding: 6px 0;
    overflow-y: auto;
    flex: 1 1 auto;
    min-height: 0;
  }
  .row {
    display: grid;
    grid-template-columns: auto 1fr auto auto;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
  }
  .row:hover {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 3%, transparent);
  }
  .color-input {
    width: 28px;
    height: 28px;
    padding: 0;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 5px;
    cursor: pointer;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
  }
  .color-input:disabled {
    cursor: default;
    opacity: 0.5;
  }
  .name-input {
    padding: 6px 10px;
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 5px;
    font-family: var(--widget-font);
    font-size: 12.5px;
    background: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    min-width: 0;
  }
  .name-input:focus {
    outline: 2px solid var(--widget-accent, oklch(75% 0.165 75));
    outline-offset: -1px;
    border-color: var(--widget-accent, oklch(75% 0.165 75));
  }
  .name-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .save-btn,
  .cancel-btn,
  .trash-btn,
  .confirm-btn {
    padding: 4px 10px;
    border-radius: 5px;
    font-size: 11.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: var(--widget-font);
  }
  .save-btn {
    background: var(--widget-accent, oklch(75% 0.165 75));
    color: var(--widget-fg, oklch(20% 0.01 250));
    border: none;
  }
  .save-btn:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  .save-btn:focus-visible {
    outline: 2px solid var(--widget-accent-deep, oklch(50% 0.19 65));
    outline-offset: 2px;
  }
  .save-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .cancel-btn {
    background: transparent;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
  }
  .cancel-btn:hover {
    color: var(--widget-fg, oklch(20% 0.01 250));
    border-color: var(--widget-accent-deep, oklch(50% 0.19 65));
  }
  .trash-btn {
    background: transparent;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    border: 1px solid transparent;
  }
  .trash-btn:hover {
    background: var(--widget-danger-tint, oklch(94% 0.04 25));
    color: var(--widget-danger, oklch(55% 0.2 25));
    border-color: var(--widget-danger, oklch(55% 0.2 25));
  }
  .trash-btn:focus-visible {
    outline: 2px solid var(--widget-danger, oklch(55% 0.2 25));
    outline-offset: 1px;
  }
  .confirm-btn {
    background: var(--widget-danger, oklch(55% 0.2 25));
    color: var(--widget-bg-elevated, oklch(99.5% 0.003 250));
    border: none;
  }
  .confirm-btn:hover:not(:disabled) {
    filter: brightness(1.06);
  }
  .confirm-btn:focus-visible {
    outline: 2px solid var(--widget-danger, oklch(55% 0.2 25));
    outline-offset: 2px;
  }

  .row-error {
    grid-column: 2 / -1;
    font-size: 11px;
    color: var(--widget-danger, oklch(55% 0.2 25));
    margin-top: -2px;
  }

  .footer-error {
    padding: 8px 14px;
    background: var(--widget-danger-tint, oklch(94% 0.04 25));
    color: var(--widget-danger, oklch(55% 0.2 25));
    font-size: 11.5px;
    border-top: 1px solid var(--widget-danger, oklch(55% 0.2 25));
  }

  .modal-footer {
    padding: 10px 14px;
    border-top: 1px solid var(--widget-divider, oklch(93% 0.006 250));
    background: var(--widget-bg-sunk, oklch(96% 0.006 250));
  }
  .hint {
    margin: 0;
    font-size: 11px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    line-height: 1.45;
  }
</style>
